export default class ReadMore {

  public static readonly SELECTOR = '.read-more-wrapper';
  private static readonly FOLD_SELECTOR = '.read-more-fold';
  private static readonly RESIZE_COOLDOWN_MS = 100;
  private static readonly MAX_FOLDED_LINES = 20;
  private static readonly MIN_FOLDED_LINES = 10;

  private readonly wrapper: HTMLElement;
  private readonly foldedElement: HTMLElement;
  private readonly button: HTMLButtonElement;
  private readonly labelReadMore: string = 'Read more';
  private readonly labelReadLess: string = 'Read less';
  private readonly clickHandler: EventListener;
  private readonly loadHandler: EventListener;
  private readonly resizeHandler: EventListener;

  private isOpen: boolean = false;
  private fullHeight: number = 0;
  private foldedHeight: number = 0;
  private resizeTimeout: number|NodeJS.Timeout|undefined;

  constructor(wrapper: HTMLElement)
  {
    this.wrapper = wrapper;
    
    const foldedElement = this.wrapper.querySelector(ReadMore.FOLD_SELECTOR);
    if(foldedElement instanceof HTMLElement) {
      this.foldedElement = foldedElement;
    } else {
      throw new Error("Fold element not found");
    }

    const button = this.wrapper.querySelector('button');

    if(button instanceof HTMLButtonElement) {
      this.button = button;
      this.labelReadMore = button.dataset.labelMore ?? this.labelReadMore;
      this.labelReadLess = button.dataset.labelLess ?? this.labelReadLess;
    } else {
      throw new Error("Button element not found");
    }

    this.clickHandler = this.handleClickEvent.bind(this);
    this.loadHandler = this.handleLoadEvent.bind(this);
    this.resizeHandler = this.handleResizeEvent.bind(this);

    this.enable();
  }

  enable(): void
  {
    //first height update without transition
    this.foldedElement.style.setProperty('transition-duration', '0ms');
    //height update
    this.updateScrollHeightVar();
    //force repaint 
    this.foldedElement.offsetHeight; 
    //re-enable transitions
    this.foldedElement.style.removeProperty('transition-duration');

    this.button.addEventListener('click', this.clickHandler, false);
    this.button.addEventListener('load', this.loadHandler, false);
    window.addEventListener('resize', this.resizeHandler, false);
  }

  destroy(): void
  {
    this.wrapper.classList.add('is-disabled');
    this.button.removeEventListener('click', this.clickHandler);
    this.button.removeEventListener('load', this.loadHandler);
    window.removeEventListener('resize', this.resizeHandler);
  }

  handleClickEvent(e: Event): void
  {
    this.toggle();
  };

  handleLoadEvent(e: Event): void
  {
    this.updateScrollHeightVar();
  }

  handleResizeEvent(e: Event): void
  {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout( () => {
      this.updateScrollHeightVar();
    }, ReadMore.RESIZE_COOLDOWN_MS );
  }

  toggle(newState: boolean = !this.isOpen): void
  {
    this.updateScrollHeightVar();
    this.wrapper.classList.toggle('unfolded', newState);
    this.button.setAttribute('aria-expanded', String(newState));
    this.button.textContent = newState ? this.labelReadLess : this.labelReadMore;
    this.isOpen = newState;

    if(!newState) {

      const yOffset = -50; 
      const element = document.querySelector('.content');
      const y = element!.getBoundingClientRect().top + window.scrollY + yOffset;

      window.scrollTo({top: y, behavior: 'smooth'});
    }
  }

  updateScrollHeightVar(): void
  {
    const foldedElementStyle = getComputedStyle(this.foldedElement);
    const lineHeight = parseFloat(foldedElementStyle.getPropertyValue('line-height'));
    const maxFoldedHeight = ReadMore.MAX_FOLDED_LINES * lineHeight;
    const minFoldedHeight = ReadMore.MIN_FOLDED_LINES * lineHeight;
    const paragraphs = this.foldedElement.children[0].children;

    let fullHeight = 0;
    let foldedHeight = 0;

    for (let i = 0; i < paragraphs.length; i++) {

      const paragraph = paragraphs[i] as HTMLElement;
      const paragraphStyle = getComputedStyle(paragraph);
      let paragraphMarginTop = parseInt(paragraphStyle.getPropertyValue('margin-top'));
      let paragraphMarginBottom = parseInt(paragraphStyle.getPropertyValue('margin-bottom'));

      let paragraphHeight =
        paragraph.scrollHeight
        + paragraphMarginTop
        + paragraphMarginBottom;

      fullHeight += paragraphHeight;
      
      let potentialFoldedHeight = foldedHeight + paragraph.scrollHeight + paragraphMarginTop;
      
      if(foldedHeight < minFoldedHeight && potentialFoldedHeight < maxFoldedHeight) {
        foldedHeight = potentialFoldedHeight;
      }
    }

    this.fullHeight = this.foldedElement.scrollHeight;
    this.foldedHeight = foldedHeight;

    this.wrapper.classList.toggle('is-disabled', (this.fullHeight == this.foldedHeight));

    this.foldedElement.style.setProperty('--full-height', this.fullHeight + 'px');
    this.foldedElement.style.setProperty('--folded-height', this.foldedHeight + 'px');
  }
}